import { useEffect, useRef } from "react";
import intl from "react-intl-universal";
import { Link } from "react-router-dom";
import { TbChevronDown, TbChevronUp } from "react-icons/tb";
import { CurrencyIcon } from "../../Atoms/CurrencyIcon";
import { StablesQuoteCarouselSkeleton } from "../DashboardSkeleton";
import {
  formatMarketQuoteChange,
  formatMarketQuotePrice,
} from "../../../Hooks/useStablesMarketQuotes";

/** Matches FxQuotesCarousel skeleton (h-8 w-8) and screenshot proportions. */
const QUOTE_CAROUSEL_ICON_SIZE = 30;

const QuoteItem = ({ quote }) => (
  <div className="quote-carousel-item quote-carousel-item--stables">
    <CurrencyIcon
      asset={quote.asset}
      iconUrl={quote.iconUrl}
      size={QUOTE_CAROUSEL_ICON_SIZE}
      className="quote-carousel-item__icon"
      preserveColor
    />
    <div className="min-w-0">
      <p className="quote-carousel-item__pair text-[11px] text-gray-500 dark:text-gray-400">{quote.pair}</p>
      <div className="quote-carousel-item__row">
        <span className="quote-carousel-item__price">
          {formatMarketQuotePrice(quote.price, quote.quoteCurrency)}
        </span>
        <span
          className={`quote-carousel-item__change ${
            quote.positive ? "text-emerald-600" : "text-red-500"
          }`}
        >
          {quote.positive ? (
            <TbChevronUp className="h-3.5 w-3.5 shrink-0" aria-hidden />
          ) : (
            <TbChevronDown className="h-3.5 w-3.5 shrink-0" aria-hidden />
          )}
          {formatMarketQuoteChange(quote.changePercent, quote.positive)}
        </span>
      </div>
    </div>
  </div>
);

export const StablesQuotesCarousel = ({
  showViewAll = true,
  viewAllLink = "/stables",
  quotes = [],
  loading = false,
  error = null,
  reload = () => {},
  refreshing = false,
}) => {
  const trackRef = useRef(null);
  const isInitialLoading = loading && quotes.length === 0;

  useEffect(() => {
    if (error || !quotes.length) return undefined;

    const track = trackRef.current;
    if (!track) return undefined;

    const interval = setInterval(() => {
      const firstItem = track.querySelector(".quote-carousel-item");
      const step = firstItem ? firstItem.offsetWidth + 24 : 180;

      if (track.scrollLeft + track.clientWidth >= track.scrollWidth - 8) {
        track.scrollTo({ left: 0, behavior: "smooth" });
      } else {
        track.scrollBy({ left: step, behavior: "smooth" });
      }
    }, 4500);

    return () => clearInterval(interval);
  }, [loading, error, quotes.length]);

  return (
    <section className="quotes-carousel-bar">
      <h3 className="shrink-0 text-sm font-bold text-brand-dark dark:text-gray-100">
        {intl.get("stables.quotesTitle")}
      </h3>

      {error ? (
        <div className="app-empty-state flex-1 !m-0 !py-3">
          <p>{typeof error === "string" ? error : intl.get("stables.quotesLoadError")}</p>
          <button type="button" className="btn-outline mt-2 !rounded-full !px-4" onClick={reload}>
            {intl.get("stables.retryLoad")}
          </button>
        </div>
      ) : isInitialLoading ? (
        <StablesQuoteCarouselSkeleton />
      ) : (
        <div
          ref={trackRef}
          className={`quotes-carousel-track${refreshing ? " quotes-carousel-track--refreshing" : ""}`}
        >
          {quotes.map((quote) => (
            <QuoteItem key={quote.id} quote={quote} />
          ))}
        </div>
      )}

      {showViewAll && (
        <Link to={viewAllLink} className="btn-outline !shrink-0 !rounded-full !px-4">
          {intl.get("stables.viewAllQuotes")}
        </Link>
      )}
    </section>
  );
};
